import React from "react";
import * as Icon from "react-feather";
import { toast as alert } from "react-toastify";

import "./imported-files-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsTabs from "../kit/ToolsTabs";

const FILE_KINDS = {
	BANK_STATEMENT: "bank_statement",
	GL_REFERENCE: "gl_reference",
	CASEWARE_REFERENCE: "caseware_reference",
};

const TABS = {
	BANK_STATEMENTS: "bank_statements",
	REFERENCE: "reference",
};

class ImportedFilesModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			activeTab: TABS.BANK_STATEMENTS,
			loading: true,
			bankStatements: [],
			shortfallsByStatementId: {},
			addingLineForStatementId: null,
			lineDraft: { transactionDate: "", description: "", amount: "" },
			addingLine: false,
			lineError: null,
			referenceFiles: [],
			fileBeingRemoved: null,
		};
	}

	async componentDidMount() {
		await this.loadFiles();
	}

	loadFiles = async () => {
		const { clientId, wpfEngagementId } = this.props;

		await this.update({ loading: true });
		const files = await BookkeepingService.listImportedFiles({ clientId, wpfEngagementId });

		if (!files || files.error) {
			await this.update({ loading: false });
			alert.error(`The imported files could not be read (${(files && files.error) || "no response"}).`);
			return;
		}

		await this.update({
			loading: false,
			bankStatements: files.bankStatements || [],
			referenceFiles: files.referenceFiles || [],
		});

		await this.loadShortfalls();
	};

	loadShortfalls = async () => {
		const { wpfEngagementId } = this.props;
		if (!wpfEngagementId) return;

		const answer = await BookkeepingService.fetchStatementsThatDoNotReconcile({ wpfEngagementId });
		if (answer.error) return;

		const shortfallsByStatementId = {};
		for (const shortfall of answer.statements) shortfallsByStatementId[shortfall.statementId] = shortfall;
		await this.update({ shortfallsByStatementId });
	};

	onStartAddingLine = ({ statement, shortfall }) => {
		this.update({
			addingLineForStatementId: statement.id,
			lineError: null,
			lineDraft: {
				transactionDate: shortfall.periodEnd || "",
				description: "",
				amount: String(shortfall.shortBy),
			},
		});
	};

	onLineDraftChange = ({ field, value }) => {
		this.update({ lineDraft: { ...this.state.lineDraft, [field]: value }, lineError: null });
	};

	onAddLine = async ({ statement }) => {
		const { wpfEngagementId } = this.props;
		const { lineDraft } = this.state;

		await this.update({ addingLine: true, lineError: null });
		const result = await BookkeepingService.addLineReadOffTheStatement({
			statementId: statement.id,
			wpfEngagementId,
			transactionDate: lineDraft.transactionDate,
			description: lineDraft.description,
			amount: lineDraft.amount,
		});
		await this.update({ addingLine: false });

		if (!result || !result.added) {
			await this.update({ lineError: (result && result.reason) || "That line could not be added." });
			return;
		}

		await this.update({ addingLineForStatementId: null });
		await this.loadFiles();
		if (this.props.onTransactionsChanged) this.props.onTransactionsChanged();
		alert.success("Line added. The statement now reconciles.");
	};

	onTabSelect = activeTab => this.update({ activeTab });

	onAskToRemove = ({ file, kind, addAnotherAfterwards = false }) =>
		this.update({ fileBeingRemoved: { ...file, kind, addAnotherAfterwards } });

	onCancelRemove = () => this.update({ fileBeingRemoved: null });

	onConfirmRemove = async () => {
		const { clientId, onFilesChanged } = this.props;
		const file = this.state.fileBeingRemoved;
		if (!file) return;

		await this.update({ fileBeingRemoved: null, loading: true });

		const result = await BookkeepingService.removeImportedFile({
			clientId,
			kind: file.kind,
			statementId: file.kind === FILE_KINDS.BANK_STATEMENT ? file.id : null,
			documentId: file.kind === FILE_KINDS.BANK_STATEMENT ? null : file.documentId,
		});

		if (!result || result.error) {
			await this.update({ loading: false });
			alert.error((result && result.error) || "That file could not be removed.");
			return;
		}

		const archivedCount = result.archivedTransactionCount || 0;
		if (archivedCount > 0) {
			alert.info(`${archivedCount} transaction${archivedCount === 1 ? "" : "s"} moved to Archived and left the trial balance.`, { autoClose: 8000 });
		} else {
			alert.success("That file was removed.");
		}

		await this.loadFiles();
		if (onFilesChanged) onFilesChanged();

		if (file.addAnotherAfterwards) this.startAddingFiles({ kind: file.kind });
	};

	startAddingFiles = ({ kind }) => {
		const { onAddFiles, onClose } = this.props;
		onClose();
		onAddFiles({ kind });
	};

	onAddClicked = () => {
		const kindForTab = this.state.activeTab === TABS.BANK_STATEMENTS ? FILE_KINDS.BANK_STATEMENT : FILE_KINDS.GL_REFERENCE;
		this.startAddingFiles({ kind: kindForTab });
	};

	onDownloadClicked = async ({ file }) => {
		const downloaded = await BookkeepingService.downloadImportedFile({ clientId: this.props.clientId, documentId: file.documentId, fileName: file.fileName });
		if (downloaded && downloaded.error) alert.error(`That file could not be opened (${downloaded.error}).`);
	};

	onReplaceClicked = ({ file, kind }) => this.onAskToRemove({ file, kind, addAnotherAfterwards: true });

	describeStatementPeriod = ({ statement }) => {
		if (!statement.periodStart && !statement.periodEnd) return "Period not read";

		const from = UtilityService.formatDate({ value: statement.periodStart, format: "MMM D, YYYY", fallback: "?" });
		const to = UtilityService.formatDate({ value: statement.periodEnd, format: "MMM D, YYYY", fallback: "?" });
		return `${from} – ${to}`;
	};

	renderIntegrity = ({ statement }) => {
		if (statement.parseStatus === "unverified") {
			return <span className="if-modal__flag if-modal__flag--neutral">Not balance-checked</span>;
		}
		if (statement.integrityPass) {
			return <span className="if-modal__flag if-modal__flag--ok"><Icon.Check size={12} /> Balances</span>;
		}

		const delta = statement.integrityDelta;
		const amountOff = delta != null ? `$${UtilityService.formatCurrency(Math.abs(delta))}` : "an unknown amount";
		return <span className="if-modal__flag if-modal__flag--warn"><Icon.AlertTriangle size={12} /> Off by {amountOff}</span>;
	};

	renderRowActions = ({ file, kind, isSignedOff }) => {
		if (isSignedOff) {
			return <span className="if-modal__locked" title="This period has been signed off, so its source files cannot be changed">Signed off</span>;
		}

		return (
			<span className="if-modal__row-actions">
				<button type="button" className="if-modal__row-action" onClick={() => this.onReplaceClicked({ file, kind })}>Replace</button>
				<button type="button" className="if-modal__row-action if-modal__row-action--danger" onClick={() => this.onAskToRemove({ file, kind })}>Remove</button>
			</span>
		);
	};

	renderBankStatements = () => {
		const { bankStatements } = this.state;
		const { isSignedOff } = this.props;

		if (bankStatements.length === 0) {
			return <div className="if-modal__empty">No bank statements are on this client's record yet.</div>;
		}

		return (
			<div className="if-modal__list">
				{bankStatements.map(statement => (
					<div key={statement.id} className="if-modal__row">
						<div className="if-modal__row-main">
							{statement.documentId ? (
								<button type="button" className="if-modal__name if-modal__name--openable" onClick={() => this.onDownloadClicked({ file: { documentId: statement.documentId, fileName: statement.fileName } })}>
									{statement.fileName || "Untitled statement"}
								</button>
							) : (
								<div className="if-modal__name">{statement.fileName || "Untitled statement"}</div>
							)}
							<div className="if-modal__meta">
								<span>{statement.accountLabel || statement.bankName || "Account not read"}</span>
								<span>{this.describeStatementPeriod({ statement })}</span>
								<span>{statement.bookedTransactionCount} in the books</span>
								{this.renderIntegrity({ statement })}
							</div>
							{this.renderShortfall({ statement })}
						</div>
						{this.renderRowActions({ file: statement, kind: FILE_KINDS.BANK_STATEMENT, isSignedOff })}
					</div>
				))}
			</div>
		);
	};

	renderReferenceFiles = () => {
		const { referenceFiles } = this.state;
		const { isSignedOff } = this.props;

		if (referenceFiles.length === 0) {
			return (
				<div className="if-modal__empty">
					No general ledger or CaseWare export has been imported for this period.
					Without one, every transaction is classified by the AI instead.
				</div>
			);
		}

		return (
			<div className="if-modal__list">
				{referenceFiles.map(file => (
					<div key={file.documentId} className="if-modal__row">
						<div className="if-modal__row-main">
							<button type="button" className="if-modal__name if-modal__name--openable" onClick={() => this.onDownloadClicked({ file })}>
								{file.fileName}
							</button>
							<div className="if-modal__meta">
								<span>{file.sourceLabel}</span>
								<span>{UtilityService.formatFileSize({ bytes: file.size })}</span>
								<span>Added {UtilityService.formatDate({ value: file.uploadedAt, format: "MMM D, YYYY" })}</span>
							</div>
						</div>
						{this.renderRowActions({ file, kind: file.category, isSignedOff })}
					</div>
				))}
			</div>
		);
	};

	renderBody = () => {
		const { activeTab, loading } = this.state;

		if (loading) return <div className="if-modal__empty">Reading this client's files…</div>;
		if (activeTab === TABS.BANK_STATEMENTS) return this.renderBankStatements();
		return this.renderReferenceFiles();
	};

	renderRemoveConfirmation = () => {
		const file = this.state.fileBeingRemoved;
		if (!file) return null;

		const isStatement = file.kind === FILE_KINDS.BANK_STATEMENT;
		const bookedCount = file.bookedTransactionCount || 0;

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={this.onCancelRemove}>Cancel</button>
					<button type="button" className="tk-button tk-button--danger" onClick={this.onConfirmRemove}>Remove</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={this.onCancelRemove} title={`Remove ${file.fileName || "this file"}?`} width={480} footer={footer}>
				<div className="if-modal__confirm">
					{isStatement && bookedCount > 0 && (
						<p className="if-modal__confirm-line">
							<strong>{bookedCount} transaction{bookedCount === 1 ? "" : "s"}</strong> from this statement are in the books.
							They will move to Archived and leave the trial balance and the financial statements. Importing this
							statement again puts them straight back.
						</p>
					)}
					{isStatement && file.affectedPeriodCount > 1 && (
						<p className="if-modal__confirm-line if-modal__confirm-line--warn">
							This statement straddles a year-end: those transactions sit in <strong>{file.affectedPeriodCount} fiscal periods</strong>,
							and removing it changes the trial balance of every one of them — including periods you are not currently looking at.
						</p>
					)}
					{isStatement && bookedCount === 0 && (
						<p className="if-modal__confirm-line">Nothing from this statement is currently in the books, so only the statement itself is removed.</p>
					)}
					{!isStatement && (
						<p className="if-modal__confirm-line">
							This file was only used to classify transactions. Removing it leaves the books exactly as they are —
							the classifications it produced stay, and their reasons still say where they came from.
						</p>
					)}
				</div>
			</ToolsModal>
		);
	};

	render() {
		const { open, onClose } = this.props;
		const { activeTab, bankStatements, referenceFiles } = this.state;
		if (!open) return null;

		const tabs = [
			{ key: TABS.BANK_STATEMENTS, label: "Bank statements", count: bankStatements.length, title: "Where the transactions in the books came from" },
			{ key: TABS.REFERENCE, label: "Reference", count: referenceFiles.length, title: "The general ledger or CaseWare export the classification was built from" },
		];

		const footer = (
			<>
				<button type="button" className="tk-button tk-button--primary" onClick={this.onAddClicked}>
					<Icon.Plus size={15} /> Add files
				</button>
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Done</button>
				</span>
			</>
		);

		return (
			<>
				<ToolsModal open={true} onClose={onClose} title="Imported files" width={720} footer={footer}>
					<ToolsTabs tabs={tabs} activeKey={activeTab} onSelect={this.onTabSelect} />
					{this.renderBody()}
				</ToolsModal>
				{this.renderRemoveConfirmation()}
			</>
		);
	}
}

export default ImportedFilesModal;
